import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2 } from "lucide-react";

/**
 * Bulk-import button for artists or suppliers.
 * Accepts .xlsx and .pdf files. Adds entries without deleting existing ones.
 * Backend route auto-derived from `kind`: /artists/import or /suppliers/import.
 */
export default function BulkImportButton({ kind = "artists", onImported, label }) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fname = file.name.toLowerCase();
    if (!fname.endsWith(".xlsx") && !fname.endsWith(".xls") && !fname.endsWith(".pdf")) {
      toast.error("Formato non supportato. Usa Excel (.xlsx) o PDF (.pdf).");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const route = kind === "suppliers" ? "/suppliers/import" : "/artists/import";
      const r = await api.post(route, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data || {};
      toast.success(
        `Importati ${d.inserted} elementi (${d.skipped_duplicates} duplicati saltati, ${d.errors} errori). Metodo: ${d.method}.`
      );
      if (onImported) onImported(d);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Errore import: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf"
        className="hidden"
        onChange={handleFile}
        data-testid={`bulk-import-input-${kind}`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={loading}
        data-testid={`bulk-import-${kind}`}
        className="btn-ghost border border-[#2563EB]/40 text-[#1D4ED8] bg-[#EFF6FF] hover:bg-[#DBEAFE]"
        title="Carica un elenco in Excel (.xlsx) o PDF. Gli elementi vengono aggiunti senza cancellare i precedenti."
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
        {label || (kind === "suppliers" ? "Carica elenco fornitori" : "Carica elenco artisti")}
      </button>
    </>
  );
}
